import { VenueReservation } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import {
  isPendingReservationUpdate,
  reservationHasPassed,
} from "@/hooks/useReservation";
import useUser from "@/hooks/useUser";
import useVenue from "@/hooks/useVenue";
import icons from "@/tools/icons";
import { Box, Button, Typography } from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { format, parse } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import MaxLinesTypography from "../MaxLinesTypography/MaxLinesTypography";
import AdditionalInfo from "./components/AdditionalInfo";
import GuestStack from "./components/CardStack/GuestStack";
import GuideStack from "./components/CardStack/GuideStack";
import EditReservationStatusButtons from "./components/EditReservationStatusButtons/EditReservationStatusButtons";
import { SelectVenueButton } from "./components/EditReservationStatusButtons/SelectVenueButton";
import PendingUpdateSummary from "./components/PendingUpdateSummary";
import ProductButton from "./components/ProductButton";
import ReservationInquiry from "./components/ReservationInquiry/ReservationInquiry";
import GroupIdBadge from "./components/GroupIdBadge";
import ToggleInquiryButton from "./components/ToggleInquiryButton";

type ReservationDetailsProps = {
  reservation: VenueReservation;
  goToCalendar?: () => void;
};
export function ReservationDetails({
  reservation,
  goToCalendar,
}: ReservationDetailsProps) {
  const { hasMultipleVenues, venueMap, productMap } = useVenue();
  const { isMtUser } = useUser();

  const product = productMap.get(reservation.group?.product?.id ?? "");
  const [openInquiry, setOpenInquiry] = useState(false);
  const venueOptions = useMemo(() => product?.venues ?? [], [product]);

  const [selectedVenueId, setSelectedVenueId] = useState(
    product?.defaultVenue?.id,
  );
  const shouldSelectVenue = useMemo(
    () =>
      !reservation.answer &&
      !reservation.venue &&
      hasMultipleVenues &&
      !product?.defaultVenue,
    [
      reservation.answer,
      reservation.venue,
      hasMultipleVenues,
      product?.defaultVenue,
    ],
  );
  const venuePortalUserIdToResetTo = useMemo(
    () => reservation.venuePortalUser?.id,
    [reservation],
  );

  const isAnswerReservationDisabled =
    reservationHasPassed(reservation) ||
    (!!reservation.answer &&
      reservation.answeredBy === "MagicalTrip" &&
      !isMtUser);

  const { t, adapterLocale } = useLocalization();

  const reservationDateTimeLabel = useMemo(() => {
    const reservationDate = reservation.group?.date;
    if (!reservationDate) return "";

    const reservationTime = reservation.reservationTime;
    if (!reservationTime) return "";

    const normalizedReservationTime =
      reservationTime.length === 5 ? `${reservationTime}:00` : reservationTime;
    const reservationDateTime = parse(
      `${reservationDate}-${normalizedReservationTime}`,
      "yyyy-MM-dd-HH:mm:ss",
      new Date(),
    );

    if (Number.isNaN(reservationDateTime.getTime())) return "";

    return format(reservationDateTime, "PPP p", { locale: adapterLocale });
  }, [adapterLocale, reservation.group?.date, reservation.reservationTime]);

  const handleReservationButtonGroupRef = useRef<HTMLDivElement | null>(null);

  const SelectVenueAcceptButton = useCallback(
    (buttonProps: ButtonProps) => (
      <SelectVenueButton
        buttonGroupRef={handleReservationButtonGroupRef}
        disabled={isAnswerReservationDisabled}
        reservationId={reservation.id}
        selectedVenueId={selectedVenueId}
        setSelectedVenueId={setSelectedVenueId}
        venueOptions={venueOptions}
        {...buttonProps}
      />
    ),
    [
      handleReservationButtonGroupRef,
      isAnswerReservationDisabled,
      reservation.id,
      selectedVenueId,
      setSelectedVenueId,
      venueOptions,
    ],
  );

  if (!reservation) return <></>;

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12} spacing={1}>
        <Stack sx={{ flexGrow: 1 }} flexDirection="column" alignItems="center">
          <Box>
            <ProductButton reservation={reservation} />
          </Box>
          {hasMultipleVenues && (
            <Box
              sx={{
                textWrap: "balance",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MaxLinesTypography textAlign="center" variant="h6" maxLines={2}>
                {reservation.venue?.name ??
                  venueMap.get(selectedVenueId ?? "")?.name ??
                  t("reservation:venueUndecided")}
              </MaxLinesTypography>
            </Box>
          )}
          <Stack alignItems="center" direction="row" spacing={4}>
            {reservationDateTimeLabel && (
              <Typography fontSize="22px" sx={{ textWrap: "nowrap" }}>
                {reservationDateTimeLabel}
              </Typography>
            )}
            <GroupIdBadge groupId={reservation.group?.id ?? ""} />
          </Stack>
        </Stack>

        <Grid item xs={12}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="stretch"
            ref={handleReservationButtonGroupRef}
          >
            <EditReservationStatusButtons
              AcceptButton={
                shouldSelectVenue ? SelectVenueAcceptButton : undefined
              }
              reservation={reservation}
              venuePortalUserIdToResetTo={venuePortalUserIdToResetTo}
              disabled={isAnswerReservationDisabled}
            />
            <ToggleInquiryButton setOpenInquiry={setOpenInquiry} />
            {goToCalendar && (
              <Button
                color="primary"
                variant="contained"
                onClick={goToCalendar}
              >
                <icons.Calendar />
              </Button>
            )}
          </Stack>
          <ReservationInquiry
            groupVenueId={reservation.groupVenueId}
            isOpen={openInquiry}
            latestInquiry={reservation.latestInquiry}
            setIsOpen={setOpenInquiry}
          />
        </Grid>
      </Grid>
      {reservation.cancellation?.reason && (
        <Grid item sm={12} sx={{ width: "100%" }}>
          <AdditionalInfo
            text={reservation.cancellation.reason}
            setAt={reservation.cancellation.created}
          />
        </Grid>
      )}
      {isPendingReservationUpdate(reservation) && (
        <Grid item sm={12} sx={{ width: "100%" }}>
          <PendingUpdateSummary reservation={reservation} />
        </Grid>
      )}
      {reservation.additionalInfo && (
        <Grid item sm={12} sx={{ width: "100%" }}>
          <AdditionalInfo
            text={reservation.additionalInfo}
            setAt={reservation.additionalInfoSetAt}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <GuideStack reservation={reservation} />
      </Grid>
      <Grid item xs={12} md={6}>
        <GuestStack reservation={reservation} />
      </Grid>
    </Grid>
  );
}
